/**
 * SoleadLab 2.0 — capture-date helpers
 *
 * A capture date is conceptually a scan DAY. Most paths already store a plain
 * `YYYY-MM-DD`, but Google-Drive-imported photos historically stored the full
 * EXIF timestamp (e.g. "2026-03-01T14:30:00"). `captureDay()` collapses any such
 * value to its date part so the date filter groups everything by day (one row
 * per scan date) instead of one row per photo-second. It is non-mutating —
 * callers use it at comparison/derivation time, so no stored value is rewritten.
 */

/**
 * Chronological comparator for photo indications (classify queue etc.).
 *
 * `captureDate` is only the scan DAY, so an entire campaign ties on it and a
 * sort using it alone degrades to array (import-insertion) order — which is
 * whatever order Drive listed the subfolders, not capture order. `createdAt`
 * carries the full capture timestamp (EXIF → DJI filename time → import time)
 * on every imported photo, so it is the primary key; day-only `captureDate` is
 * the fallback. Undated photos sort last. ISO-8601 strings compare as text; a
 * bare day ("2026-07-13") sorts before that day's full timestamps, which keeps
 * mixed data deterministic.
 */
export function compareCaptureOrder(
  a: { createdAt?: string; captureDate?: string },
  b: { createdAt?: string; captureDate?: string },
): number {
  const ka = a.createdAt || a.captureDate || '';
  const kb = b.createdAt || b.captureDate || '';
  if (!ka && !kb) return 0;
  if (!ka) return 1;
  if (!kb) return -1;
  return ka < kb ? -1 : ka > kb ? 1 : 0;
}

/** The scan day (YYYY-MM-DD) of a capture-date value. '' for empty/unparseable. */
export function captureDay(value: string | null | undefined): string {
  if (!value) return '';
  // Fast path: value already begins with a literal YYYY-MM-DD. Take it verbatim
  // (no Date parsing) so a timestamp never shifts across midnight by timezone.
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  if (m) return m[1];
  // Fallback for other encodings: parse and format in local time.
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
